/**
 * Serviço de imagens 360°.
 *
 * Responsabilidade:
 * - CRUD de imagens no Firestore (coleção `images`)
 * - Upload de arquivos no Firebase Storage
 * - Conversão WEBP antes do upload
 * - Atualização automática de coverImage na primeira imagem
 *
 * Storage path (novos uploads):
 *   panorama:  users/{userId}/images/{imageId}.webp
 *   thumbnail: users/{userId}/images/{imageId}_thumb.webp
 * Organização por projeto é controlada apenas pelo Firestore (`projectId`).
 *
 * @see docs/storage-architecture.md
 */

import {
  collection,
  deleteField,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "@/config/firebase";
import {
  createThumbnailBlob,
  processImageForUpload,
} from "@/utils/imageConversion";
import { diagLog } from "@/diagnostics/freezeDiagnostics";
import {
  getProjectById,
  adjustProjectImageCount,
  updateProject,
} from "@/services/projects/projectService";
import {
  assertCanReplaceImageStorage,
  assertCanUploadImage,
} from "@/services/plans/planService";
import {
  collectImageStoragePaths,
  deleteImageFile,
  deleteImageFilesTolerant,
} from "@/services/storage/storageService";
import { getActiveWorkspaceIdForUser } from "@/services/workspaces/workspaceService";
import { sortImagesByRecency, toMillis } from "@/utils/imageRecencySort";
import {
  collectImageDeleteHotspotRefs,
  collectSceneHotspotDeletionRefs,
} from "@/services/hotspots/hotspotService";
import {
  buildPaginationCursor,
  isPaginationCursor,
  toAppDate,
} from "@/services/firebase/dates";
import {
  getImagePreviewUrl,
  hasPersistedImageUrl,
  imageMatchesCover,
} from "@/utils/imageDisplayUrls";
import {
  getQuotaSizeBytes,
  getStoredSizeBytes,
} from "@/utils/storageQuota";

/** Limite de operações por `writeBatch` do Firestore. */
const FIRESTORE_BATCH_LIMIT = 500;

/**
 * @typedef {Object} Image
 * @property {string} id
 * @property {string} userId
 * @property {string | null} projectId
 * @property {string} title
 * @property {string} originalUrl — download URL do panorama (viewer 360°)
 * @property {string} previewUrl — download URL do thumbnail (cards / capa)
 * @property {string} storagePath — path do panorama no Storage
 * @property {string} previewStoragePath — path do thumbnail no Storage
 * @property {number} sizeBytes — tamanho físico (legado; espelha storedSizeBytes)
 * @property {number | null} originalSizeBytes — File.size original (quota comercial)
 * @property {number} storedSizeBytes — tamanho físico no Storage
 * @property {number} width
 * @property {number} height
 * @property {string} originalFileType
 * @property {string} optimizedFileType
 * @property {'private' | 'shared' | 'public'} visibility
 * @property {'private' | 'shared' | 'public' | null} [projectVisibility]
 * @property {Date | null} [createdAt]
 * @property {Date | null} [updatedAt]
 */

/**
 * @typedef {Object} UploadImageOptions
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [originalFileType]
 * @property {Blob} [processedBlob] — WebP do panorama já processado na prévia
 * @property {Blob} [thumbnailBlob] — WebP do thumbnail já processado na prévia
 * @property {(step: string) => void} [onProgress]
 */

/**
 * @typedef {Object} ReplaceImageFileOptions
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [originalFileType]
 * @property {string} [title]
 * @property {Blob} [processedBlob] — WebP do panorama já processado na prévia
 * @property {Blob} [thumbnailBlob] — WebP do thumbnail já processado na prévia
 * @property {(step: string) => void} [onProgress]
 */

function normalizeProjectId(projectId) {
  if (projectId === undefined || projectId === null || projectId === "") {
    return null;
  }

  return projectId;
}

/**
 * Path padrão do panorama para novos uploads. Ignora projectId — vínculo ao projeto fica no Firestore.
 *
 * @param {string} userId
 * @param {string} imageId
 * @returns {string}
 */
function getImageStoragePath(userId, imageId) {
  return `users/${userId}/images/${imageId}.webp`;
}

/**
 * Path padrão do thumbnail persistido.
 *
 * @param {string} userId
 * @param {string} imageId
 * @returns {string}
 */
function getImageThumbStoragePath(userId, imageId) {
  return `users/${userId}/images/${imageId}_thumb.webp`;
}

/**
 * @param {File} file
 * @param {{ processedBlob?: Blob | null, thumbnailBlob?: Blob | null, width?: number, height?: number }} options
 * @returns {Promise<{ processedBlob: Blob, thumbnailBlob: Blob, width?: number, height?: number }>}
 */
async function resolveProcessedAssets(file, options = {}) {
  const { processedBlob = null, thumbnailBlob = null, width = 0, height = 0 } =
    options;

  if (processedBlob && thumbnailBlob) {
    return { processedBlob, thumbnailBlob };
  }

  if (processedBlob && !thumbnailBlob) {
    const thumb = await createThumbnailBlob(processedBlob, width, height);
    return { processedBlob, thumbnailBlob: thumb.processedBlob };
  }

  const processed = await processImageForUpload(file);
  return {
    processedBlob: processed.processedBlob,
    thumbnailBlob: processed.thumbnailBlob,
    width: processed.width,
    height: processed.height,
  };
}

/**
 * Envia panorama e thumbnail. Se o thumbnail falhar, remove o panorama recém-enviado
 * quando `cleanupPanoramaOnThumbFailure` for verdadeiro (create, ou replace com path novo).
 *
 * @param {{
 *   storagePath: string,
 *   previewStoragePath: string,
 *   panoramaBlob: Blob,
 *   thumbnailBlob: Blob,
 *   cleanupPanoramaOnThumbFailure: boolean,
 * }} params
 * @returns {Promise<{ originalUrl: string, previewUrl: string }>}
 */
async function uploadPanoramaAndThumbnail({
  storagePath,
  previewStoragePath,
  panoramaBlob,
  thumbnailBlob,
  cleanupPanoramaOnThumbFailure,
}) {
  const panoramaRef = ref(storage, storagePath);
  await uploadBytes(panoramaRef, panoramaBlob, { contentType: "image/webp" });
  const originalUrl = await getDownloadURL(panoramaRef);
  diagLog("thumbnail upload", { storagePath, previewStoragePath });

  try {
    const thumbRef = ref(storage, previewStoragePath);
    await uploadBytes(thumbRef, thumbnailBlob, { contentType: "image/webp" });
    const previewUrl = await getDownloadURL(thumbRef);
    return { originalUrl, previewUrl };
  } catch (error) {
    console.error(
      "[uploadImage] Falha no upload do thumbnail — panorama não será persistido:",
      previewStoragePath,
      error,
    );
    if (cleanupPanoramaOnThumbFailure) {
      await deleteImageFile(storagePath);
    }
    throw error;
  }
}

function mapImageDoc(imageId, data) {
  const storedSizeBytes = getStoredSizeBytes(data);
  const hasOriginal =
    typeof data.originalSizeBytes === "number" &&
    Number.isFinite(data.originalSizeBytes);

  return {
    id: imageId,
    userId: data.userId ?? "",
    projectId: normalizeProjectId(data.projectId),
    title: data.title ?? "",
    originalUrl: data.originalUrl ?? "",
    previewUrl: data.previewUrl ?? "",
    storagePath: data.storagePath ?? "",
    previewStoragePath: data.previewStoragePath ?? "",
    sizeBytes: storedSizeBytes,
    originalSizeBytes: hasOriginal ? data.originalSizeBytes : null,
    storedSizeBytes,
    width: data.width ?? 0,
    height: data.height ?? 0,
    originalFileType: data.originalFileType ?? "",
    optimizedFileType: data.optimizedFileType ?? "image/webp",
    visibility: data.visibility ?? "private",
    projectVisibility: normalizeProjectId(data.projectId)
      ? (data.projectVisibility ?? "private")
      : null,
    createdAt: toAppDate(data.createdAt),
    updatedAt: toAppDate(data.updatedAt),
  };
}

/**
 * Ordem cronológica de criação (mais antiga primeiro). Usado só para capa do projeto.
 *
 * @param {Image[]} images
 * @returns {Image[]}
 */
function sortImagesByCreatedAtAsc(images) {
  return [...images].sort((a, b) => {
    return toMillis(a.createdAt) - toMillis(b.createdAt);
  });
}

/**
 * @param {Image[]} images
 * @returns {Image | null}
 */
function pickOldestImageForCover(images) {
  const sorted = sortImagesByCreatedAtAsc(images);
  return sorted[0] ?? null;
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
export function formatImageResolution(width, height) {
  if (!width || !height) {
    return "—";
  }

  return `${width} × ${height}`;
}

/**
 * Lista imagens de um projeto (requer userId para regras Firestore).
 *
 * @param {string} projectId
 * @param {string} userId
 * @returns {Promise<Image[]>}
 */
export async function getImagesByProjectId(projectId, userId) {
  const imagesQuery = query(
    collection(db, "images"),
    where("userId", "==", userId),
    where("projectId", "==", projectId),
  );
  const snapshot = await getDocs(imagesQuery);

  const images = snapshot.docs.map((docSnap) =>
    mapImageDoc(docSnap.id, docSnap.data()),
  );

  return sortImagesByRecency(images);
}

/**
 * @typedef {Object} ProjectImagesPageResult
 * @property {Image[]} items
 * @property {import("@/services/firebase/dates").PaginationCursor | null} cursor
 * @property {boolean} hasMore
 */

/**
 * Lista imagens de um projeto paginadas por updatedAt DESC.
 * Requer índice composto: images — userId ASC, projectId ASC, updatedAt DESC.
 *
 * @param {string} projectId
 * @param {string} userId
 * @param {{ limitCount: number, cursor?: import("@/services/firebase/dates").PaginationCursor | null }} options
 * @returns {Promise<ProjectImagesPageResult>}
 */
export async function getImagesPageByProjectId(
  projectId,
  userId,
  { limitCount, cursor = null },
) {
  const constraints = [
    where("userId", "==", userId),
    where("projectId", "==", projectId),
    orderBy("updatedAt", "desc"),
  ];

  if (isPaginationCursor(cursor)) {
    const cursorSnap = await getDoc(doc(db, "images", cursor.id));
    if (cursorSnap.exists()) {
      constraints.push(startAfter(cursorSnap));
    } else if (cursor.sortValue != null) {
      constraints.push(startAfter(Timestamp.fromMillis(cursor.sortValue)));
    }
  }

  constraints.push(limit(limitCount + 1));

  const imagesQuery = query(collection(db, "images"), ...constraints);

  try {
    const snapshot = await getDocs(imagesQuery);
    const docs = snapshot.docs;
    const hasMore = docs.length > limitCount;
    const pageDocs = hasMore ? docs.slice(0, limitCount) : docs;

    const items = sortImagesByRecency(
      pageDocs.map((docSnap) => mapImageDoc(docSnap.id, docSnap.data())),
    );

    const last = pageDocs.at(-1);

    return {
      items,
      cursor: last
        ? buildPaginationCursor(last.id, last.data().updatedAt)
        : null,
      hasMore,
    };
  } catch (error) {
    if (error?.code === "failed-precondition") {
      console.error(
        "[getImagesPageByProjectId] Índice Firestore necessário: collection(images) where userId ==, projectId ==, orderBy updatedAt desc",
        error,
      );
    }

    throw error;
  }
}

/**
 * Lista imagens de um projeto para páginas públicas (query só por projectId).
 *
 * @param {string} projectId
 * @returns {Promise<Image[]>}
 */
export async function getImagesByProjectIdPublic(projectId) {
  const imagesQuery = query(
    collection(db, "images"),
    where("projectId", "==", projectId),
  );
  const snapshot = await getDocs(imagesQuery);

  const images = snapshot.docs.map((docSnap) =>
    mapImageDoc(docSnap.id, docSnap.data()),
  );

  return sortImagesByCreatedAtAsc(images);
}

/**
 * Lista imagens soltas do usuário (projectId == null), mais recentes primeiro.
 *
 * @param {string} userId
 * @returns {Promise<Image[]>}
 */
export async function getLooseImagesByUserId(userId) {
  const imagesQuery = query(
    collection(db, "images"),
    where("userId", "==", userId),
    where("projectId", "==", null),
  );
  const snapshot = await getDocs(imagesQuery);

  const images = snapshot.docs.map((docSnap) =>
    mapImageDoc(docSnap.id, docSnap.data()),
  );

  return sortImagesByRecency(images);
}

/**
 * @typedef {Object} LooseImagesPageResult
 * @property {Image[]} items
 * @property {import("@/services/firebase/dates").PaginationCursor | null} cursor
 * @property {boolean} hasMore
 */

/**
 * Lista imagens soltas paginadas por updatedAt DESC (fallback createdAt na ordenação local).
 * Requer índice composto: images — userId ASC, projectId ASC, updatedAt DESC.
 *
 * @param {string} userId
 * @param {{ limitCount: number, cursor?: import("@/services/firebase/dates").PaginationCursor | null }} options
 * @returns {Promise<LooseImagesPageResult>}
 */
export async function getLooseImagesPageByUserId(
  userId,
  { limitCount, cursor = null },
) {
  const constraints = [
    where("userId", "==", userId),
    where("projectId", "==", null),
    orderBy("updatedAt", "desc"),
  ];

  if (isPaginationCursor(cursor)) {
    const cursorSnap = await getDoc(doc(db, "images", cursor.id));
    if (cursorSnap.exists()) {
      constraints.push(startAfter(cursorSnap));
    } else if (cursor.sortValue != null) {
      constraints.push(startAfter(Timestamp.fromMillis(cursor.sortValue)));
    }
  }

  constraints.push(limit(limitCount + 1));

  const imagesQuery = query(collection(db, "images"), ...constraints);

  try {
    const snapshot = await getDocs(imagesQuery);
    const docs = snapshot.docs;
    const hasMore = docs.length > limitCount;
    const pageDocs = hasMore ? docs.slice(0, limitCount) : docs;

    const items = sortImagesByRecency(
      pageDocs.map((docSnap) => mapImageDoc(docSnap.id, docSnap.data())),
    );

    const last = pageDocs.at(-1);

    return {
      items,
      cursor: last
        ? buildPaginationCursor(last.id, last.data().updatedAt)
        : null,
      hasMore,
    };
  } catch (error) {
    if (error?.code === "failed-precondition") {
      console.error(
        "[getLooseImagesPageByUserId] Índice Firestore necessário: collection(images) where userId ==, projectId == null, orderBy updatedAt desc",
        error,
      );
    }

    throw error;
  }
}

/**
 * Lista as imagens mais recentes do usuário (com ou sem projeto).
 *
 * @param {string} userId
 * @param {number} [limit]
 * @returns {Promise<Image[]>}
 */
export async function getRecentImagesByUserId(userId, limit = 3) {
  const imagesQuery = query(
    collection(db, "images"),
    where("userId", "==", userId),
  );
  const snapshot = await getDocs(imagesQuery);

  const images = snapshot.docs.map((docSnap) =>
    mapImageDoc(docSnap.id, docSnap.data()),
  );

  return sortImagesByRecency(images).slice(0, limit);
}

/**
 * Busca uma imagem pelo ID (Firestore `images/{imageId}`).
 *
 * @param {string} imageId
 * @returns {Promise<Image | null>}
 */
export async function getImageById(imageId) {
  const snapshot = await getDoc(doc(db, "images", imageId));

  if (!snapshot.exists()) {
    return null;
  }

  return mapImageDoc(snapshot.id, snapshot.data());
}

/**
 * Atualiza o título de uma imagem (somente o dono).
 *
 * @param {string} imageId
 * @param {string} userId
 * @param {string} title
 * @returns {Promise<void>}
 */
export async function updateImageTitle(imageId, userId, title) {
  const imageRef = doc(db, "images", imageId);
  const snapshot = await getDoc(imageRef);

  if (!snapshot.exists()) {
    throw new Error("Imagem não encontrada.");
  }

  const data = snapshot.data();
  if (data.userId !== userId) {
    throw new Error("Sem permissão para editar esta imagem.");
  }

  await updateDoc(imageRef, {
    title: title.trim(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Atualiza a visibilidade de uma imagem (somente o dono).
 *
 * @param {string} imageId
 * @param {string} userId
 * @param {'private' | 'shared'} visibility
 * @returns {Promise<void>}
 */
export async function updateImageVisibility(imageId, userId, visibility) {
  if (visibility !== "private" && visibility !== "shared") {
    throw new Error("Visibilidade inválida para imagens.");
  }

  const imageRef = doc(db, "images", imageId);
  const snapshot = await getDoc(imageRef);

  if (!snapshot.exists()) {
    throw new Error("Imagem não encontrada.");
  }

  const data = snapshot.data();
  if (data.userId !== userId) {
    throw new Error("Sem permissão para editar esta imagem.");
  }

  await updateDoc(imageRef, {
    visibility,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Executa exclusões/atualizações em um ou mais writeBatches (limite 500 ops).
 *
 * @param {Array<{
 *   type: 'delete' | 'update',
 *   ref: import("firebase/firestore").DocumentReference,
 *   data?: Record<string, unknown>,
 * }>} operations
 * @returns {Promise<void>}
 */
async function commitFirestoreOperationsInChunks(operations) {
  for (let offset = 0; offset < operations.length; offset += FIRESTORE_BATCH_LIMIT) {
    const chunk = operations.slice(offset, offset + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);

    for (const operation of chunk) {
      if (operation.type === "delete") {
        batch.delete(operation.ref);
      } else {
        batch.update(operation.ref, operation.data ?? {});
      }
    }

    await batch.commit();
  }
}

/**
 * Remove o documento de stats diretamente associado à imagem, se existir.
 * Falhas (incl. rules ainda sem delete) não restauram o cascade já commitado.
 *
 * @param {string} userId
 * @param {string} imageId
 * @returns {Promise<boolean>}
 */
async function deleteImageStatsDocTolerant(userId, imageId) {
  if (!userId || !imageId) {
    return false;
  }

  try {
    await deleteDoc(doc(db, "stats", userId, "images", imageId));
    return true;
  } catch (error) {
    console.warn(
      "[deleteImage] Falha ao remover stats da imagem — cleanup pendente:",
      imageId,
      error,
    );
    return false;
  }
}

/**
 * @typedef {Object} DeleteImageResult
 * @property {string | null} coverImage — nova capa quando a excluída era capa; `null` se não mudou
 * @property {number} deletedOwnHotspotCount
 * @property {number} deletedIncomingSceneHotspotCount
 * @property {number} deletedStoragePathCount
 * @property {number} sizeBytes — bytes de quota liberados (originalSizeBytes com fallback)
 * @property {number} originalSizeBytes
 * @property {number} storedSizeBytes
 * @property {boolean} deletedImageStats
 */

/**
 * Exclui imagem com cascade completo: hotspots próprios, scene de entrada,
 * documento Firestore, referências de projeto/capa/count, stats da imagem e Storage.
 *
 * Ordem: coletar refs → commit Firestore (hotspots + imagem + projeto) →
 * stats tolerante → Storage tolerante. Falhas de Storage/stats não restauram docs.
 *
 * @param {string} userId
 * @param {string | null} projectId — contexto da UI; o `projectId` da imagem prevalece
 * @param {string} imageId
 * @param {string} [projectCoverImage]
 * @returns {Promise<DeleteImageResult>}
 */
export async function deleteImage(
  userId,
  projectId,
  imageId,
  projectCoverImage = "",
) {
  if (!userId || !imageId) {
    throw new Error("Dados incompletos para excluir a imagem.");
  }

  const imageRef = doc(db, "images", imageId);
  const snapshot = await getDoc(imageRef);

  if (!snapshot.exists()) {
    throw new Error("Imagem não encontrada.");
  }

  const rawData = snapshot.data();
  const image = mapImageDoc(imageId, rawData);

  if (image.userId !== userId) {
    throw new Error("Sem permissão para excluir esta imagem.");
  }

  const effectiveProjectId = image.projectId ?? normalizeProjectId(projectId);
  const storagePaths = collectImageStoragePaths(rawData);
  const { ownRefs, incomingRefs, allRefs } = await collectImageDeleteHotspotRefs(
    userId,
    imageId,
    effectiveProjectId,
  );

  /** @type {string | null} */
  let coverImageResult = null;
  /** @type {Record<string, unknown> | null} */
  let projectUpdate = null;

  if (effectiveProjectId) {
    const project = await getProjectById(effectiveProjectId);

    if (project) {
      const coverToCheck =
        (projectCoverImage && String(projectCoverImage).trim()) ||
        project.coverImage ||
        "";
      const wasCover = Boolean(
        coverToCheck && imageMatchesCover(image, coverToCheck),
      );
      const nextImageCount = Math.max(0, (project.imageCount ?? 0) - 1);

      projectUpdate = {
        imageCount: nextImageCount,
        updatedAt: serverTimestamp(),
      };

      if (wasCover) {
        const remaining = (
          await getImagesByProjectId(effectiveProjectId, userId)
        ).filter((item) => item.id !== imageId);
        const oldestRemaining = pickOldestImageForCover(remaining);
        const newCover = oldestRemaining
          ? getImagePreviewUrl(oldestRemaining)
          : "";

        projectUpdate.coverImage = newCover;
        coverImageResult = newCover;
      }
    }
  }

  /** @type {Array<{ type: 'delete' | 'update', ref: import("firebase/firestore").DocumentReference, data?: Record<string, unknown> }>} */
  const operations = [
    ...allRefs.map((hotspotRef) => ({ type: /** @type {'delete'} */ ("delete"), ref: hotspotRef })),
    { type: /** @type {'delete'} */ ("delete"), ref: imageRef },
  ];

  if (effectiveProjectId && projectUpdate) {
    operations.push({
      type: "update",
      ref: doc(db, "projects", effectiveProjectId),
      data: projectUpdate,
    });
  }

  try {
    await commitFirestoreOperationsInChunks(operations);
    console.log(
      "[deleteImage] Firestore cascade concluído:",
      imageId,
      {
        ownHotspots: ownRefs.length,
        incomingSceneHotspots: incomingRefs.length,
        projectId: effectiveProjectId,
      },
    );
  } catch (error) {
    console.error("[deleteImage] Falha no cascade Firestore:", imageId, error);
    throw error;
  }

  const deletedImageStats = await deleteImageStatsDocTolerant(userId, imageId);

  await deleteImageFilesTolerant(storagePaths);

  const quotaBytes = getQuotaSizeBytes(image);
  const storedBytes = getStoredSizeBytes(image);

  return {
    coverImage: coverImageResult,
    deletedOwnHotspotCount: ownRefs.length,
    deletedIncomingSceneHotspotCount: incomingRefs.length,
    deletedStoragePathCount: storagePaths.length,
    sizeBytes: quotaBytes,
    originalSizeBytes: quotaBytes,
    storedSizeBytes: storedBytes,
    deletedImageStats,
  };
}

/**
 * Faz upload de uma imagem: processa panorama + thumbnail, envia ambos ao Storage
 * e só então cria o documento Firestore. Se for a primeira imagem do projeto,
 * atualiza project.coverImage com o thumbnail.
 *
 * @param {string} userId
 * @param {string | null} projectId
 * @param {File} file
 * @param {string} title
 * @param {UploadImageOptions} [options]
 * @returns {Promise<Image>}
 */
export async function uploadImage(userId, projectId, file, title, options = {}) {
  const {
    width: optionWidth = 0,
    height: optionHeight = 0,
    originalFileType = "",
    processedBlob = null,
    thumbnailBlob = null,
    onProgress,
  } = options;
  const normalizedProjectId = normalizeProjectId(projectId);
  const originalSizeBytes = file?.size ?? 0;

  onProgress?.("Preparando imagem...");

  await assertCanUploadImage(userId, originalSizeBytes);

  let panoramaBlob = processedBlob;
  let thumbBlob = thumbnailBlob;
  let width = optionWidth;
  let height = optionHeight;

  if (!panoramaBlob || !thumbBlob) {
    onProgress?.("Convertendo imagem...");
    diagLog("upload convert fallback", { sizeBytes: originalSizeBytes });
    const processed = await resolveProcessedAssets(file, {
      processedBlob: panoramaBlob,
      thumbnailBlob: thumbBlob,
      width,
      height,
    });
    panoramaBlob = processed.processedBlob;
    thumbBlob = processed.thumbnailBlob;
    if (processed.width) {
      width = processed.width;
    }
    if (processed.height) {
      height = processed.height;
    }
  }

  const storedSizeBytes = panoramaBlob.size + thumbBlob.size;

  const imageRef = doc(collection(db, "images"));
  const imageId = imageRef.id;
  const storagePath = getImageStoragePath(userId, imageId);
  const previewStoragePath = getImageThumbStoragePath(userId, imageId);

  onProgress?.("Enviando para o servidor...");
  diagLog("upload storage start", {
    imageId,
    panoramaBytes: panoramaBlob.size,
    thumbnailBytes: thumbBlob.size,
    reusedPreviewBlob: Boolean(processedBlob && thumbnailBlob),
  });

  const { originalUrl, previewUrl } = await uploadPanoramaAndThumbnail({
    storagePath,
    previewStoragePath,
    panoramaBlob,
    thumbnailBlob: thumbBlob,
    cleanupPanoramaOnThumbFailure: true,
  });
  diagLog("upload storage done", { imageId });

  onProgress?.("Salvando informações...");
  diagLog("upload firestore start", { imageId });

  const workspaceId = await getActiveWorkspaceIdForUser(userId);

  const existingImages = normalizedProjectId
    ? await getImagesByProjectId(normalizedProjectId, userId)
    : await getLooseImagesByUserId(userId);
  const isFirstImage = normalizedProjectId ? existingImages.length === 0 : false;

  const projectVisibility = normalizedProjectId
    ? (await getProjectById(normalizedProjectId))?.visibility ?? "private"
    : null;

  const imageData = {
    userId,
    workspaceId,
    projectId: normalizedProjectId,
    title: title.trim(),
    originalUrl,
    previewUrl,
    storagePath,
    previewStoragePath,
    originalSizeBytes,
    storedSizeBytes,
    // Legado: sizeBytes permanece como tamanho físico armazenado.
    sizeBytes: storedSizeBytes,
    width,
    height,
    originalFileType,
    optimizedFileType: "image/webp",
    visibility: "private",
    ...(normalizedProjectId ? { projectVisibility } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  try {
    await setDoc(imageRef, imageData);
  } catch (error) {
    console.error(
      "[uploadImage] Falha no Firestore após upload — removendo objetos novos:",
      imageId,
      error,
    );
    await deleteImageFilesTolerant([storagePath, previewStoragePath]);
    throw error;
  }
  diagLog("upload firestore done", { imageId, projectId: normalizedProjectId });

  if (normalizedProjectId) {
    if (isFirstImage) {
      await adjustProjectImageCount(normalizedProjectId, 1, {
        coverImage: previewUrl,
      });
    } else {
      await adjustProjectImageCount(normalizedProjectId, 1);
    }
  }

  const now = new Date();
  return mapImageDoc(imageId, {
    ...imageData,
    createdAt: now,
    updatedAt: now,
  });
}

/**
 * Substitui o arquivo de uma imagem existente, preservando metadados (title, visibility, etc.).
 * Processa panorama + thumbnail, envia ambos, atualiza Firestore e só então remove
 * objetos antigos cujo path não coincide com os novos.
 *
 * @param {string} userId
 * @param {string | null} projectId
 * @param {string} imageId
 * @param {File} file
 * @param {string} [projectCoverImage]
 * @param {ReplaceImageFileOptions} [options]
 * @returns {Promise<{ image: Image, coverImage: string | null }>}
 */
export async function replaceImageFile(
  userId,
  projectId,
  imageId,
  file,
  projectCoverImage = "",
  options = {},
) {
  const normalizedProjectId = normalizeProjectId(projectId);
  const {
    width: optionWidth = 0,
    height: optionHeight = 0,
    originalFileType = "",
    title: newTitle,
    processedBlob = null,
    thumbnailBlob = null,
    onProgress,
  } = options;

  const imageRef = doc(db, "images", imageId);
  const snapshot = await getDoc(imageRef);

  if (!snapshot.exists()) {
    throw new Error("Imagem não encontrada.");
  }

  const rawExisting = snapshot.data();
  const existingImage = mapImageDoc(imageId, rawExisting);

  if (existingImage.userId !== userId) {
    throw new Error("Sem permissão para substituir esta imagem.");
  }

  if (existingImage.projectId !== normalizedProjectId) {
    throw new Error("Imagem não pertence a este contexto.");
  }

  onProgress?.("Preparando imagem...");

  const originalSizeBytes = file?.size ?? 0;
  const previousQuotaBytes = getQuotaSizeBytes(existingImage);

  await assertCanReplaceImageStorage(
    userId,
    originalSizeBytes,
    previousQuotaBytes,
  );

  let panoramaBlob = processedBlob;
  let thumbBlob = thumbnailBlob;
  let width = optionWidth;
  let height = optionHeight;

  if (!panoramaBlob || !thumbBlob) {
    onProgress?.("Convertendo imagem...");
    diagLog("replace convert fallback", { imageId, sizeBytes: originalSizeBytes });
    const processed = await resolveProcessedAssets(file, {
      processedBlob: panoramaBlob,
      thumbnailBlob: thumbBlob,
      width,
      height,
    });
    panoramaBlob = processed.processedBlob;
    thumbBlob = processed.thumbnailBlob;
    if (processed.width) {
      width = processed.width;
    }
    if (processed.height) {
      height = processed.height;
    }
  }

  const storedSizeBytes = panoramaBlob.size + thumbBlob.size;

  const storagePath = getImageStoragePath(userId, imageId);
  const previewStoragePath = getImageThumbStoragePath(userId, imageId);
  const oldPaths = collectImageStoragePaths(rawExisting) || [];
  const newPaths = [storagePath, previewStoragePath];
  const cleanupPanoramaOnThumbFailure = !oldPaths.includes(storagePath);

  onProgress?.("Enviando para o servidor...");
  diagLog("replace storage start", {
    imageId,
    panoramaBytes: panoramaBlob.size,
    thumbnailBytes: thumbBlob.size,
    reusedPreviewBlob: Boolean(processedBlob && thumbnailBlob),
  });

  const { originalUrl, previewUrl } = await uploadPanoramaAndThumbnail({
    storagePath,
    previewStoragePath,
    panoramaBlob,
    thumbnailBlob: thumbBlob,
    cleanupPanoramaOnThumbFailure,
  });
  diagLog("replace storage done", { imageId });

  onProgress?.("Salvando informações...");
  diagLog("replace firestore start", { imageId });

  const firestoreUpdates = {
    originalUrl,
    previewUrl,
    storagePath,
    previewStoragePath,
    originalSizeBytes,
    storedSizeBytes,
    sizeBytes: storedSizeBytes,
    width,
    height,
    originalFileType,
    optimizedFileType: "image/webp",
    updatedAt: serverTimestamp(),
  };

  if (typeof newTitle === "string" && newTitle.trim()) {
    firestoreUpdates.title = newTitle.trim();
  }

  try {
    await updateDoc(imageRef, firestoreUpdates);
  } catch (error) {
    console.error(
      "[replaceImageFile] Falha no Firestore após upload:",
      imageId,
      error,
    );
    const uploadedOnlyNew = newPaths.filter((path) => !oldPaths.includes(path));
    if (uploadedOnlyNew.length > 0) {
      await deleteImageFilesTolerant(uploadedOnlyNew);
    }
    throw error;
  }
  diagLog("replace firestore done", { imageId });

  const stalePaths = oldPaths.filter((path) => !newPaths.includes(path));
  if (stalePaths.length > 0) {
    onProgress?.("Removendo arquivo antigo...");
    await deleteImageFilesTolerant(stalePaths);
  }

  const updatedImage = mapImageDoc(imageId, {
    ...existingImage,
    ...firestoreUpdates,
    updatedAt: new Date(),
  });

  const wasCover = Boolean(
    normalizedProjectId && imageMatchesCover(existingImage, projectCoverImage),
  );

  if (normalizedProjectId) {
    if (wasCover) {
      await updateProject(normalizedProjectId, { coverImage: previewUrl });
      return { image: updatedImage, coverImage: previewUrl };
    }

    await updateProject(normalizedProjectId, {});
  }

  return { image: updatedImage, coverImage: null };
}

/**
 * Move uma imagem solta para um projeto (somente Firestore).
 * Se o projeto não tiver capa, define coverImage com o thumbnail real quando existir.
 *
 * @param {string} userId
 * @param {string} imageId
 * @param {string} targetProjectId
 * @returns {Promise<{ image: Image, coverImage: string | null }>}
 */
export async function moveImageToProject(userId, imageId, targetProjectId) {
  const imageRef = doc(db, "images", imageId);
  const snapshot = await getDoc(imageRef);

  if (!snapshot.exists()) {
    throw new Error("Imagem não encontrada.");
  }

  const image = mapImageDoc(imageId, snapshot.data());

  if (image.userId !== userId) {
    throw new Error("Sem permissão para mover esta imagem.");
  }

  if (image.projectId !== null) {
    throw new Error("Esta imagem já pertence a um projeto.");
  }

  const project = await getProjectById(targetProjectId);

  if (!project) {
    throw new Error("Projeto não encontrado.");
  }

  if (project.userId !== userId) {
    throw new Error("Sem permissão para mover para este projeto.");
  }

  if (image.projectId === targetProjectId) {
    throw new Error("A imagem já pertence a este projeto.");
  }

  if (!hasPersistedImageUrl(image)) {
    throw new Error("URL da imagem ausente.");
  }

  await updateDoc(imageRef, {
    projectId: targetProjectId,
    projectVisibility: project.visibility,
    updatedAt: serverTimestamp(),
  });

  const updatedImage = mapImageDoc(imageId, {
    ...image,
    projectId: targetProjectId,
    projectVisibility: project.visibility,
    updatedAt: new Date(),
  });

  const projectHasCover = Boolean(project.coverImage?.trim());
  const coverUrl = getImagePreviewUrl(image);

  if (!projectHasCover && coverUrl) {
    await adjustProjectImageCount(targetProjectId, 1, { coverImage: coverUrl });
    return { image: updatedImage, coverImage: coverUrl };
  }

  await adjustProjectImageCount(targetProjectId, 1);
  return { image: updatedImage, coverImage: null };
}

/**
 * Remove uma imagem de um projeto e envia para imagens soltas (somente Firestore).
 * Se era capa do projeto, promove a imagem mais antiga restante ou limpa a capa.
 *
 * @param {string} userId
 * @param {string} imageId
 * @param {string} [projectCoverImage]
 * @returns {Promise<{ image: Image, coverImage: string | null }>}
 */
export async function moveImageToUnassigned(
  userId,
  imageId,
  projectCoverImage = "",
) {
  const imageRef = doc(db, "images", imageId);
  const snapshot = await getDoc(imageRef);

  if (!snapshot.exists()) {
    throw new Error("Imagem não encontrada.");
  }

  const image = mapImageDoc(imageId, snapshot.data());

  if (image.userId !== userId) {
    throw new Error("Sem permissão para mover esta imagem.");
  }

  const sourceProjectId = image.projectId;

  if (!sourceProjectId) {
    throw new Error("Esta imagem já está nas imagens soltas.");
  }

  if (!hasPersistedImageUrl(image)) {
    throw new Error("URL da imagem ausente.");
  }

  const sceneHotspotRefs = await collectSceneHotspotDeletionRefs(
    userId,
    imageId,
    sourceProjectId,
  );

  const batch = writeBatch(db);

  for (const hotspotRef of sceneHotspotRefs) {
    batch.delete(hotspotRef);
  }

  batch.update(imageRef, {
    projectId: null,
    projectVisibility: deleteField(),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();

  const updatedImage = mapImageDoc(imageId, {
    ...image,
    projectId: null,
    projectVisibility: null,
    updatedAt: new Date(),
  });

  const wasCover = Boolean(
    imageMatchesCover(image, projectCoverImage),
  );

  if (!wasCover) {
    await adjustProjectImageCount(sourceProjectId, -1);
    return { image: updatedImage, coverImage: null };
  }

  const remaining = await getImagesByProjectId(sourceProjectId, userId);
  const oldestRemaining = pickOldestImageForCover(remaining);
  const newCover = oldestRemaining ? getImagePreviewUrl(oldestRemaining) : "";

  await adjustProjectImageCount(sourceProjectId, -1, { coverImage: newCover });

  return { image: updatedImage, coverImage: newCover };
}

/**
 * Adapta uma imagem Firestore para o formato do ImageCard.
 *
 * @param {Image} image
 * @returns {{
 *   id: string,
 *   name: string,
 *   url: string,
 *   previewUrl: string,
 *   originalUrl: string,
 *   previewStoragePath: string,
 * }}
 */
export function mapImageToCard(image) {
  return {
    id: image.id,
    name: image.title || "Sem título",
    url: getImagePreviewUrl(image),
    previewUrl: image.previewUrl ?? "",
    originalUrl: image.originalUrl ?? "",
    previewStoragePath: image.previewStoragePath ?? "",
  };
}

export {
  getImageCardSource,
  getImagePanoramaUrl,
  getImagePreviewUrl,
  isRealImageThumbnail,
} from "@/utils/imageDisplayUrls";
